import {useSnackbar} from 'notistack'
import {createContext, useContext, useEffect, useRef, useState} from 'react'
import useDebounce from 'src/hooks/useDebounce'
import {useCompaniesSelector} from 'src/layouts/main-layout/companies-selector/companies-selector-context'
import api, {Client} from 'src/services/api'
import {Page} from 'src/types'
import handleErrorWithSnackbar from 'src/utilities/handleErrorWithSnackbar'
import {isNumber} from 'src/utilities/regexValidation'
import {DropdownItem} from '../../../components/dropdown/DropdownProps'

type ClientsContextData = {
  showOptionsButton: boolean
  clients: Client[]
  clientsPage: Page
  setClientsPage: (page: number) => void
  setClientsPageSize: (pageSize: number) => void
  selectedClientsIds: number[]
  setSelectedClientsIds: (ids: number[]) => void
  clientsTotalCount: number
  loadingClients: boolean
  reloadingClients: boolean
  reloadClients: () => void
  clientSearch: string
  setClientSearch: (text: string) => void
  deleteClient: (clientId?: number) => void
  searchFilterData: DropdownItem[]
  searchFilterSelected: string
  setSearchFilterSelected: (searchFilter: string) => void
}

const searchFilterData = [
  {id: '1', label: 'Nome do Cliente'},
  {id: '2', label: 'ID'},
]

const ClientsContext = createContext({} as ClientsContextData)

function ClientsContextProvider({children}: any) {
  const [clients, setClients] = useState<Client[]>([])

  const [clientsPage, _setClientsPage] = useState<Page>({
    page: 1,
    pageSize: 20,
  })
  const [clientsTotalCount, setClientsTotalCount] = useState<number>(0)
  const [reloadClientsCount, setReloadClientsCount] = useState(0)

  const [clientSearch, setClientSearch] = useState<string>('')
  const debouncedSearch = useDebounce<string>(clientSearch, 500)

  const [loadingClients, setLoadingClients] = useState(true)
  const [reloadingClients, setReloadingClients] = useState(false)
  const [selectedClientsIds, setSelectedClientsIds] = useState<number[]>([])

  const {enqueueSnackbar} = useSnackbar()
  const showOptionsButton = selectedClientsIds.length > 1

  const {selectedCompanies} = useCompaniesSelector()

  const [searchFilterSelected, setSearchFilterSelected] = useState<string>('Nome do Cliente')
  const blockClientsRequests = useRef(false)

  function setClientsPage(page: number) {
    _setClientsPage((prev) => {
      return {...prev, page}
    })
  }

  function setClientsPageSize(pageSize: number) {
    _setClientsPage((prev) => {
      return {...prev, pageSize}
    })
  }

  useEffect(() => {
    setClientsPage(1)
  }, [debouncedSearch])

  useEffect(() => {
    async function loadClients() {
      try {
        if (blockClientsRequests.current) return
        blockClientsRequests.current = true
        setLoadingClients(true)
        const response = await api.client.getMany({
          page: clientsPage.page,
          pageSize: clientsPage.pageSize,
          includes: ['cameras', 'moniClientInfo', 'analyticCameras', 'onePortariaClientInfo'],
          search: {
            name: searchFilterSelected === 'Nome do Cliente' ? debouncedSearch : undefined,
          },
          filter: {
            ids: searchFilterSelected === 'ID' && isNumber(debouncedSearch) ? [Number(debouncedSearch)] : undefined,
            companyIds: selectedCompanies.map((it) => it.id),
          },
        })
        setClients(response.data.data.entities)
        setClientsTotalCount(response.data.data.totalPages ?? 0)
      } catch (error) {
        handleErrorWithSnackbar(enqueueSnackbar, error, 'Erro ao carregar clientes cadastrados')
      } finally {
        setLoadingClients(false)
        setReloadingClients(false)
        blockClientsRequests.current = false
      }
    }

    loadClients()
  }, [clientsPage, enqueueSnackbar, reloadClientsCount, selectedCompanies])

  async function deleteClient(clientId?: number) {
    const deleteId = clientId ? [clientId] : selectedClientsIds || []
    if (!deleteId.length) throw new Error('Erro ao deletar cliente')
    try {
      await api.client.deleteMany(deleteId)
      enqueueSnackbar('Cliente removido com sucesso, é necessário atualizar a página', {
        variant: 'success',
      })
      setSelectedClientsIds([])
      reloadClients()
    } catch (error) {
      handleErrorWithSnackbar(enqueueSnackbar, error, 'Não foi possível remover o cliente')
    }
  }

  const reloadClients = () => {
    setReloadingClients(true)
    setReloadClientsCount((x) => x + 1)
  }

  return (
    <ClientsContext.Provider
      value={{
        showOptionsButton,
        clients,
        clientsPage,
        setClientsPage,
        setClientsPageSize,
        clientsTotalCount,
        loadingClients,
        reloadingClients,
        setSelectedClientsIds,
        selectedClientsIds,
        deleteClient,
        reloadClients,
        clientSearch,
        setClientSearch,
        searchFilterData,
        searchFilterSelected,
        setSearchFilterSelected,
      }}>
      {children}
    </ClientsContext.Provider>
  )
}

function useClientsContext() {
  return useContext(ClientsContext)
}

export {useClientsContext}
export default ClientsContextProvider
